import React from 'react';
import styles from "./Editor.module.scss"
import {MDBCol, MDBContainer, MDBRow} from 'mdbreact';
import {PRODUCT_STATUS_WAITING_FOR_APPROVAL} from 'data/redux/constants/productsConstants';
import Steps from './components/steps';
import {IProduct} from 'data/interfaces/products';
import ScrollToTopExtended from 'gui/components/scrollToTopExtended/ScrollToTopExtended';
import {Persist} from 'formik-persist';
import {Unless, When} from 'react-if';
import FormikErrorScrollToTop from 'formik-top-error-focus';
import {xxHash} from 'data/utils/calculators';
import {versionStamp} from 'data/utils/constants';
import {promiseWait} from 'data/utils/shortcuts';
import {isMaxStep} from './components/abstractDetailsForm/components/stepButtons/components/nextButton';
import PublishedCard from './components/publishedCard';
import {eProductSubCategoryType, eProductCategoryType} from 'data/enums/products';
import PageWithSidebarStructureWithPoints from "./components/pageWithSidebarStructureWithPoints";
import EditorFormSync from "./components/editorFormSync";
import {cloneDeep, isEqual, pick} from "lodash";
import {filterPublishProductKeys, stepFromProps, keysToFiles, filesToKeys} from "./utils";
import SubTypeSelector, {ISubTypeSelectorProps} from "./components/subTypeSelector";

const noPersist = location.hash.toLowerCase().includes("nopersist");
const noClearPersist = location.hash.toLowerCase().includes("noclearpersist");

export interface IEditorProps {
  steps: string[];
  product?: Partial<IProduct>;
  isAdmin?: boolean;

  getStepContent: (number, ...any) => any;
  getProductInfo: (number, ...any) => any;
  onSubmit: (product: Partial<IProduct>) => Promise<any>;

  setUrl: (path: string) => void;

  pathname?: string;

  loading?: boolean;
  productTypeId?: number;
  productType?: eProductCategoryType;
  productSubType?: eProductSubCategoryType;
  loadProductTypes?: (productType: number, productSubType: number) => void;
  getAboveStepsComponent?: (state: any) => any;
  subTypeSelectorProps?: ISubTypeSelectorProps;

  enableOutsideEditorFormSync?: boolean;
}

const initialState = {
  activeStep: 0,
  previousStep: undefined,
  loading: false,
  submittedProduct: {},
  forms: {},
};

class Editor extends React.PureComponent<IEditorProps, any> {
  public static defaultProps = {
    getAboveStepsComponent: () => null,
    getStepContent: () => null,
    getProductInfo: () => null,
    onSubmit: () => null,
    setUrl: () => null,
  };
  getInitialState = (additions = {}) => {
    return {
      ...(this.props.product || {}),
      ...initialState,
      ...additions,
    };
  };

  state = this.getInitialState();

  static getDerivedStateFromProps(nextProps, prevState) {
    const activeStep = stepFromProps(nextProps);
    if (typeof activeStep === 'number' && activeStep >= 0) {
      if (activeStep > 0) {
        if (typeof prevState.previousStep === 'number' || activeStep >= 3) {
          for (const key in prevState) {
            if ((key in prevState) && typeof prevState[key] === 'object' && Object.keys(prevState[key]).length > 0) {
              return {activeStep: Math.min(activeStep, 3)};
            }
          }
        }
      }
      return {activeStep: 0};
    }
    return null;
  }

  setUrlToStep = activeStep => {
    const currentActiveStep = stepFromProps(this.props);
    if (currentActiveStep >= 0 && typeof this.props.setUrl === 'function') {
      if (activeStep !== currentActiveStep) {
        this.props.setUrl(this.currentRoute(activeStep));
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const activeStep = stepFromProps(prevProps);
    if (activeStep && activeStep > prevState.activeStep) {
      this.setUrlToStep(prevState.activeStep);
    }
    /*
    if (this.props.loading&&!this.props.productTypeId && this.props.loadProductTypes && this.props.productType && this.props.productSubType) {
      this.props.loadProductTypes(this.props.productType, this.props.productSubType);
    }

     */
  }

  getProduct = (): Partial<IProduct> => {
    const {activeStep, ...p} = this.state;
    return filterPublishProductKeys(p);
  }

  moveNextStep = ({activeStep, ...data}) => {
    return new Promise(resolve => this.setState({
      loading: true,
      previousStep: this.state.activeStep,
      activeStep: Math.min(activeStep, this.props.steps.length - 1),
      ...data
    }, resolve as any))
      .then(r => {
        this.setUrlToStep(activeStep);
        if (activeStep > 2) {
          const product: Partial<IProduct> = (this.props.product || {});
          return this.props.onSubmit(
            {
              ...filterPublishProductKeys({
                ...product,
                ...this.getProduct(),
                ...(this.props.isAdmin ? {
                  status: this.getProduct().status || PRODUCT_STATUS_WAITING_FOR_APPROVAL
                } : {}),
              }, true),
              ...((product.product_type_id) ?
                pick(product, ['product_type_id', 'product_type', 'product_sub_type']) : {
                  product_type: this.props.productType,
                  product_sub_type: this.props.productSubType
                })
            }
          )
            .then(async s => {
              if (s.error) {
                return this.moveNextStep({activeStep: 2,});
              } else {
                if (!noClearPersist) {
                  await this.clearAllSavedFormStates(s);
                }
                if (s.stop !== true) {
                  return new Promise(resolve => this.setState(this.getInitialState({submittedProduct: {...this.state, ...s.value}}), resolve as any));
                }
                return s;
              }
            })
            .catch(e => {
              //   return this.moveNextStep({activeStep: 2,});
              return Promise.resolve();
            })
        }
        return r;
      })
      .catch(e => Promise.resolve())
      .then((r: any) => (r && r.stop === true) ? r : promiseWait(500, r))
      .then((r) => {
        if ((r && r.stop === true)) {
        } else {
          this.setState({loading: false});
        }
      });
  }

  setAdminStep = (activeStep) => {
    if (this.props.isAdmin) {
      this.setState({
        activeStep
      });
    }
  }

  public static currentRoute = (pathName: string, step: number = 0) => `/${pathName.split("step")[0].split("/").map(s => (s || '').trim()).filter(s => s).join("/")}/step/${step + 1}`

  currentRoute = (step: number = this.state.activeStep) => Editor.currentRoute(location.pathname, step)

  currentEditorKey = (step?: number) => `Editor-${versionStamp}-${xxHash(this.currentRoute(step))}`

  clearAllSavedFormStates = (r?) => {
    for (let i = 0; i < 10; i++) {
      try {
        localStorage.removeItem(this.currentEditorKey(i));
      } catch (e) {
      }
    }
    return promiseWait(500)
      .then(() => {
        if ((r && r.stop === true)) {
        } else {
          this.state = initialState;
        }
      });
  }

  private setStepFormState = (newValues, callback?) => {
    const forms = this.state.forms;
    if (!isEqual(forms[this.state.activeStep], newValues)) {
      forms[this.state.activeStep] = newValues;
      return this.setState({forms: cloneDeep(forms)}, callback);
    } else if (typeof callback === 'function') {
      callback();
    }
  }

  private persistFilter = ({isSubmitting, ...filteredData}: any) => {
    filteredData = cloneDeep(filteredData);
    if (this.props.enableOutsideEditorFormSync) {
      if (this.state.activeStep <= 0) {
        if (filteredData.values) {
          this.setStepFormState(filteredData.values);
        }
      }
    }
    if (filteredData.values.files) {
      filteredData.values.files = filesToKeys(filteredData.values.files);
    }
    return {
      ...filteredData,
      // values: {},
      touched: {},
      errors: {},
      submitCount: 0,
      initialErrors: {},
      initialTouched: {},
      initialValues: {},
      validateOnMount: false,
      validateOnBlur: false,
      validateOnChange: false,
      dirty: false,
    };
  }

  private loadFilter = (loaded) => {
    if (loaded.values.files) {
      try {
        const values = loaded.values;
        const files = values.files;
        return {
          ...loaded,
          values: {
            ...values,
            files: keysToFiles(files),
          },
        };
      } catch (e) {
        
      }
    }
    return loaded;
  }

  private getStepProductInfo = () => {
    if (isMaxStep({steps: this.props.steps, activeStep: this.state.activeStep - 1})) {
      return (null)
    } else
      return this.props.getProductInfo(this.state.activeStep, {
        commonFormComponents: this.commonFormComponents,
        ...this.props,
        activeStep: this.state.activeStep,
        product: {
          ...(this.props.product || {}),
          ...this.getProduct(),
        },
        loading: this.state.loading || this.props.loading,
      }, this.moveNextStep);
  }

  private getStepContent = () => {
    if (isMaxStep({steps: this.props.steps, activeStep: this.state.activeStep - 1})) {
      return (<PublishedCard product={this.state.submittedProduct} loading={this.state.loading || this.props.loading}/>)
    } else
      return this.props.getStepContent(this.state.activeStep, {
        commonFormComponents: this.commonFormComponents,
        ...this.props,
        activeStep: this.state.activeStep,
        product: {
          ...(this.props.product || {}),
          ...this.getProduct(),
        },
        loading: this.state.loading || this.props.loading,
      }, this.moveNextStep);
  }

  commonFormComponents = (form: any = {}) => <>
    <Unless condition={this.props.product && this.props.product.id > 0}>
      {() =>
        <When condition={this.state.activeStep <= 0 && !noPersist}>
          {() => <Persist name={this.currentEditorKey()}
                          persistFilter={this.persistFilter}
                          loadFilter={this.loadFilter}
          />}
        </When>
      }
    </Unless>
    <FormikErrorScrollToTop/>
    
    <When condition={this.props.enableOutsideEditorFormSync && this.state.activeStep <= 0}>
      {() => <EditorFormSync form={form}
                             outsideFormValues={this.state.forms[this.state.activeStep]}
                             setOutsideFormValues={this.setStepFormState}/>}
    </When>
  </>
  private onStateChange = (newState) => {
    return this.setState(newState);
  }

  render() {
    const currentRoute = this.currentRoute();
    return <PageWithSidebarStructureWithPoints
      getStepProductInfo={this.getStepProductInfo}
    >
      <MDBContainer fluid className={`createProductContainer ${styles.editor}`}>
        {this.props.getAboveStepsComponent(this)}
        {this.props.subTypeSelectorProps ?
          <SubTypeSelector {...this.props.subTypeSelectorProps}
                           state={this.state}
                           setState={this.onStateChange}/>
          :
          null}
        <MDBRow>
          <MDBCol className={styles.editorSection}>
            <Steps isAdmin={this.props.isAdmin}
                   activeStep={this.state.activeStep}
                   setAdminStep={this.setAdminStep}
                   steps={this.props.steps}/>
            {this.getStepContent()}
          </MDBCol>
        </MDBRow>
        <ScrollToTopExtended scollOnChange={this.state.activeStep} noButton scrollOnRender smooth/>
      </MDBContainer>
    </PageWithSidebarStructureWithPoints>;
  }
}

export default Editor;
